import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  ChangePasswordBody,
  ChangePasswordData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface ChangePasswordState
  extends MergeRequestState<ChangePasswordData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.changePassword,
  isFetching: (state: ReduxState) => state.server.changePassword.isFetching,
  data: (state: ReduxState) => state.server.changePassword.data,
  error: (state: ReduxState) => state.server.changePassword.error,
};

const initialState: ChangePasswordState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/changePassword';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    { body, config }: { body: ChangePasswordBody; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.changePassword(body, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerChangePassword {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const changePassword: ServerChangePassword = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
